<!-- eslint-disable vue/html-self-closing -->
<template>
  <section
    class="issue-header"
    :class="[{ '--actions': hasEditAccess }]"
  >
    <div class="issue-header__header">
      <div class="additional">
        <b class="id">#{{ issue.localId }}</b>
      </div>
      <div class="title">
        <h4>
          <span>{{ issue.title || $t('message.title_empty') }}</span>
        </h4>
      </div>
    </div>
    <div class="issue-header__info">
      <status-label
        class="issue-header__status"
        :status="issue.status"
      />
      <div
        v-if="issue?.author"
        class="issue-header__author"
      >
        <div class="author">
          <a :href="authorLink">
            <img
              :src="issue.author?.avatar"
              class="avatar"
              height="22px"
              width="22px"
            />
          </a>
          <a
            class="username truncated-link"
            :href="authorLink"
          >
            <b>{{ issue.author?.fullName }}</b>
          </a>
        </div>
        <div class="create">
          {{ $tc('issue_create', 2) }}
          {{ $moment.utc(issue.createdAt).fromNow() }}
        </div>
      </div>
      <div class="issue-header__comments">
        <i class="gf-icon gf-icon-comment"></i>
        <small>{{ discussionCounter || 0 }}</small>
      </div>
    </div>
    <div
      v-if="hasEditAccess || getActionAvailabilityFlag(ACTION_ENUM.DELETE_ISSUE)"
      class="issue-header__actions"
    >
      <gf-link-button
        :href="editLink"
        variant="outline-primary"
        :text="$t('message.edit')"
        after-icon="gf-icon gf-icon-pen"
      />
      <gf-button
        v-if="getActionAvailabilityFlag(ACTION_ENUM.DELETE_ISSUE)"
        variant="danger"
        :text="$t('message.delete')"
        after-icon="gf-icon gf-icon-trash"
        @click="openDeleteIssueModal"
      />
    </div>

    <gf-custom-modal-window
      v-if="getActionAvailabilityFlag(ACTION_ENUM.DELETE_ISSUE)"
      ref="modalDeleteIssue"
      class="issue-header__modal"
      gf-modal-data-name="deleteIssueModal"
      :gf-modal-header="$t('issueDeleteConfirmation')"
      gf-modal-wrapper-width="500px"
    >
      <template #body>
        <div class="content">
          <gf-button
            variant="outline-secondary"
            :text="$t('message.cancel_action')"
            @click="closeDeleteIssueModal"
          />

          <gf-button
            variant="danger"
            :text="$t('message.delete')"
            @click="deleteIssue"
          />
        </div>
      </template>
    </gf-custom-modal-window>
  </section>
</template>

<script>
// eslint-disable-next-line max-len
import StatusLabel from '@shared/shared-components/status-label';
import IssueMessages from '@shared/modules/issue/issue.messages';
import ACTION_ENUM from '@shared/modules/custom-roles/custom-roles.constants';
// eslint-disable-next-line import/extensions
import GfCustomModalWindow from '@shared/shared-components/shared/GfCustomModalWindow.vue';
import IssueService from '@shared/modules/issue/issue.service';
import GfButton from '@/shared-modules/shared-components/shared/components/GfButton';
import GfLinkButton from '@/shared-modules/shared-components/shared/components/GfLinkButton';

export default {
  name: 'IssueHeader',
  components: {
    StatusLabel,
    GfLinkButton,
    GfButton,
    GfCustomModalWindow
  },
  data() {
    return {};
  },
  computed: {
    issue() {
      return this.$store.getters.getIssue || {};
    },
    authorLink() {
      return `/user/${this.issue?.author?.username}`;
    },
    hasEditAccess() {
      return document.getElementById('hasEditAccess')?.getAttribute('value') === 'true';
    },
    editLink() {
      return `/project/${this.$route.params.alias}/${this.$route.params.projectAlias}/issue/${this.$route.params.issueId}/edit`;
    },
    discussionCounter() {
      return this.$store.getters.getDiscussionsCounter;
    },
    ACTION_ENUM() {
      return ACTION_ENUM;
    }
  },
  methods: {
    getActionAvailabilityFlag(action) {
      return this.$store.getters.getActionAvailabilityFlag(action);
    },
    closeDeleteIssueModal() {
      this.$refs.modalDeleteIssue.closeModalWindow();
    },
    openDeleteIssueModal() {
      this.$refs.modalDeleteIssue.openModalWindow();
    },
    deleteIssue() {
      IssueService.deleteIssue(
        this.issue.userAlias,
        this.issue.projectAlias,
        this.issue.localId
      ).then(() => {
        window.location = `/project/${this.issue.userAlias}/${this.issue.projectAlias}/issue`;
      });
      this.closeDeleteIssueModal();
    }
  },
  i18n: {
    messages: IssueMessages
  }
};
</script>

<style lang="scss">
$this: '.issue-header';

#{$this} {
  display: grid;
  grid-template-areas:
    'header'
    'info';
  gap: 12px;

  @include theme.max-sm {
    gap: 16px;
  }

  &.--actions {
    grid-template-columns: 1fr min-content;
    grid-template-areas:
      'header actions'
      'info info';

    @include theme.max-md {
      grid-template-columns: 100%;
      grid-template-areas:
        'header'
        'info'
        'actions';
    }
  }

  #{$this}__header {
    grid-area: header;
    display: flex;
    align-items: flex-start;
    gap: 8px;

    @include theme.max-sm {
      flex-direction: column;
      gap: 4px;
    }

    & > .additional {
      display: flex;
      align-items: flex-start;
      gap: 8px;

      & > .id {
        line-height: 1.2;
        font-size: 24px;
        color: var(--text-muted);
      }
    }

    & > .title {
      display: flex;
      gap: 4px;
      align-items: flex-start !important;
      word-break: break-word;

      & > i {
        min-height: 28px;
      }

      & > h4 {
        margin-bottom: 0;
      }
    }
  }
  #{$this}__info {
    grid-area: info;
    display: grid;
    grid-template-areas: 'status author comments';
    grid-template-columns: min-content min-content auto;
    align-items: center;
    gap: 12px;
    white-space: nowrap;

    @include theme.max-lg {
      grid-template-columns: min-content 1fr auto;
      align-items: flex-start;
    }

    @include theme.max-sm {
      grid-template-areas: 'status comments' 'author author';
      grid-template-columns: 1fr min-content;
    }

    #{$this}__status {
      grid-area: status;
      min-height: 24px;
    }

    #{$this}__author {
      grid-area: author;
      display: inline-flex;
      align-items: baseline;
      gap: 4px;
      white-space: nowrap;
      color: var(--text-muted);

      @include theme.max-lg {
        flex-wrap: wrap;
      }

      & > .author {
        display: inline-flex;
        align-items: baseline;
        gap: 4px;

        & .avatar {
          border-radius: 11px;
        }

        & > .username {
          color: var(--text-muted);
        }
      }

      & > .create {
        display: inline-flex;
        gap: 4px;
        font-size: 12px;

        @include theme.max-lg {
          flex-wrap: wrap;
        }
      }
      & .truncated-link {
        display: inline-block;
        max-width: 210px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        @include theme.max-xxl {
          max-width: 150px;
        }

        @include theme.max-xl {
          max-width: 90px;
        }

        @include theme.max-lg {
          max-width: 300px;
        }

        @include theme.max-md {
          max-width: 340px;
        }

        @include theme.max-sm {
          max-width: 240px;
        }
      }

      & .truncated-link-long {
        display: inline-block;
        max-width: 360px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        @include theme.max-xxl {
          max-width: 300px;
        }

        @include theme.max-xl {
          max-width: 220px;
        }

        @include theme.max-lg {
          max-width: 360px;
        }

        @include theme.max-md {
          max-width: 386px;
        }

        @include theme.max-sm {
          max-width: 285px;
        }
      }
    }

    #{$this}__comments {
      grid-area: comments;

      & > i {
        margin-right: 4px;
      }
    }
  }
  #{$this}__actions {
    margin-left: auto;
    grid-area: actions;
    display: flex;
    gap: 8px;
    align-items: flex-start;

    @include theme.max-md {
      margin-left: unset;
    }

    & > a,
    & > button {
      @include theme.max-md {
        width: 100%;
      }
    }
  }
  #{$this}__modal {
    & .content {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
    }
  }
}
</style>
